import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { GrScrollEngine, bootGrScrollEngine } from '@theme/gr-scroll-engine';

const CAPTURE_LISTENER = { capture: true, passive: false };

class GrHero extends Component {
  
  requiredRefs = ['root', 'inner'];

  
  #gsap = null;

  
  #ScrollTrigger = null;

  
  #engine = null;

  
  #initialized = false;

  
  #triggers = [];

  
  #introComplete = false;

  
  #introAnimating = false;

  
  #onFirstInteraction = null;

  
  #onKeyDown = null;

  
  #preventScroll = null;

  
  #introTimeout = null;

  
  #gateFallbackTimeout = null;

  
  #onScrollReady = null;

  
  #onHintClick = null;

  
  #onEnterClick = null;

  
  #onFallbackEnter = null;

  
  #onReinit = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#hasGatedScroll()) {
      this.classList.add('gr-hero--static');
      return;
    }

    if (!this.#isInteractive()) {
      this.#setupFallbackGate();
      return;
    }

    this.#onScrollReady = () => this.#init();
    this.#onReinit = () => this.#rearmGate();
    window.addEventListener('gr-scroll:ready', this.#onScrollReady);
    window.addEventListener('gr-hero:reinit', this.#onReinit);

    bootGrScrollEngine().then((engine) => {
      if (engine) this.#init();
      else this.#setupFallbackGate();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#isInteractive() && GrScrollEngine.get() && !this.#initialized) {
      this.#init();
    }
  }

  #hasGatedScroll() {
    return this.hasAttribute('data-gated-scroll') && !prefersReducedMotion();
  }

  #isInteractive() {
    return document.documentElement.hasAttribute('data-gr-scroll') && this.#hasGatedScroll();
  }

  #setupFallbackGate() {
    this.classList.add('gr-hero--static', 'gr-hero--fallback');

    this.#onFallbackEnter = (event) => {
      event.preventDefault();
      this.#playFallbackIntro();
    };

    const enter = this.refs.enter;
    const hint = this.refs.hint;
    const inner = this.refs.inner;

    enter?.addEventListener('click', this.#onFallbackEnter);
    hint?.addEventListener('click', this.#onFallbackEnter);
    inner?.addEventListener('click', this.#onFallbackEnter);

    document.addEventListener('wheel', this.#onFallbackEnter, { once: true, passive: false });
    document.addEventListener('touchstart', this.#onFallbackEnter, { once: true, passive: false });
  }

  #playFallbackIntro() {
    const next =
      this.querySelector('.gr-hero__scroll-space')?.nextElementSibling ||
      this.nextElementSibling;

    if (next instanceof HTMLElement) {
      next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: this.#introDistance(), behavior: 'smooth' });
  }

  #init() {
    if (this.#initialized || !this.#isInteractive()) return;

    const engine = GrScrollEngine.get();
    if (!engine) return;

    this.#initialized = true;
    this.#engine = engine;
    this.#gsap = engine.gsap;
    this.#ScrollTrigger = engine.ScrollTrigger;

    this.classList.add('gr-hero--interactive');
    document.documentElement.classList.add('gr-hero-active');
    window.scrollTo(0, 0);

    this.#setupGatedIntro();
    this.#setupScrollTriggers();
    engine.refresh();
  }

  
  #handleHintClick = (event) => {
    if (this.#introComplete || this.#introAnimating) return;
    event.preventDefault();
    this.#playIntro();
  };

  
  #handleEnterClick = (event) => {
    event.preventDefault();
    this.#playIntro();
  };

  #setupGatedIntro() {
    const engine = this.#engine;
    const hint = this.refs.hint;
    const enter = this.refs.enter;
    const inner = this.refs.inner;

    if (!engine) return;

    engine.stop();
    document.documentElement.classList.add('gr-hero-gate');

    this.#onFirstInteraction = (event) => {
      if (this.#introComplete || this.#introAnimating) return;

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('#header-group, #header-component, .gr-header-section, header-drawer, .gr-header__drawer')
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.#playIntro();
    };

    this.#onKeyDown = (event) => {
      if (this.#introComplete || this.#introAnimating) return;
      if (!['ArrowDown', 'PageDown', ' ', 'Spacebar'].includes(event.key)) return;
      event.preventDefault();
      this.#playIntro();
    };

    this.#preventScroll = (event) => {
      event.preventDefault();
    };

    this.#armGateListeners();

    if (hint instanceof HTMLAnchorElement) {
      this.#onHintClick = this.#handleHintClick;
      hint.addEventListener('click', this.#onHintClick);
    }

    if (enter instanceof HTMLButtonElement) {
      this.#onEnterClick = this.#handleEnterClick;
      enter.addEventListener('click', this.#onEnterClick);
    }

    if (inner instanceof HTMLElement) {
      inner.addEventListener('click', this.#handleEnterClick);
    }

    if (hint) {
      this.#gsap?.set(hint, { opacity: 1 });
    }

    if (this.#isDesignMode()) {
      this.#gateFallbackTimeout = setTimeout(() => this.#playIntro(), 2000);
      return;
    }

    this.#gateFallbackTimeout = setTimeout(() => this.#releaseGate(), 12000);
  }

  #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  #removeGateListeners() {
    if (this.#onFirstInteraction) {
      document.removeEventListener('wheel', this.#onFirstInteraction, CAPTURE_LISTENER);
      document.removeEventListener('touchstart', this.#onFirstInteraction, CAPTURE_LISTENER);
    }

    if (this.#onKeyDown) {
      document.removeEventListener('keydown', this.#onKeyDown, CAPTURE_LISTENER);
    }
  }

  #releaseGate() {
    if (this.#introComplete || this.#introAnimating) return;

    this.#removeGateListeners();
    document.documentElement.classList.remove('gr-hero-gate');
    this.#engine?.start();
    this.#introComplete = true;
    this.classList.add('gr-hero--intro-complete');
    document.documentElement.classList.add('gr-hero-intro-complete');
    window.__grHeroAnimating = false;
  }

  
  #playIntro() {
    const engine = this.#engine;
    const hint = this.refs.hint;
    const distance = this.#introDistance();

    if (!engine || !this.#gsap || this.#introAnimating || this.#introComplete) return;

    this.#introAnimating = true;
    window.__grHeroAnimating = true;

    if (this.#gateFallbackTimeout) {
      clearTimeout(this.#gateFallbackTimeout);
      this.#gateFallbackTimeout = null;
    }

    this.#removeGateListeners();

    this.classList.add('gr-hero--intro-active');
    document.documentElement.classList.add('gr-hero-intro-active');
    document.documentElement.classList.remove('gr-hero-gate', 'gr-hero-intro-complete');
    this.classList.remove('gr-hero--intro-complete');

    window.addEventListener('wheel', this.#preventScroll, { passive: false });
    window.addEventListener('touchmove', this.#preventScroll, { passive: false });

    if (hint) {
      this.#gsap.to(hint, { opacity: 0, duration: 0.35, ease: 'power1.inOut' });
    }

    engine.start();

    if (this.#introTimeout) clearTimeout(this.#introTimeout);
    this.#introTimeout = setTimeout(() => {
      engine.scrollTo(distance, {
        lock: true,
        force: true,
        // Long enough for the fade to finish covering the hero without a mid snap.
        duration: 1.25,
        onComplete: () => {
          engine.stop();

          this.#introTimeout = setTimeout(() => {
            engine.start();
            engine.refresh();

            this.#introAnimating = false;
            this.#introComplete = true;
            window.__grHeroAnimating = false;
            this.classList.remove('gr-hero--intro-active');
            this.classList.add('gr-hero--intro-complete');
            document.documentElement.classList.remove('gr-hero-intro-active');
            document.documentElement.classList.add('gr-hero-intro-complete');

            window.removeEventListener('wheel', this.#preventScroll);
            window.removeEventListener('touchmove', this.#preventScroll);

            if (hint) {
              this.#gsap?.to(hint, { opacity: 1, duration: 0.5, ease: 'power2.out' });
            }

            window.dispatchEvent(new CustomEvent('gr-hero:intro-complete'));
          }, 200);
        },
      });
    }, 120);
  }

  
  /**
   * Scroll through the hero white fade to completion, and at least to the story
   * top so the cover never stops halfway then snaps.
   */
  #introDistance() {
    const multiplier = Number.parseFloat(this.dataset.scrollDistance || '1.7');
    const fadeEnd =
      window.innerHeight * (Number.isFinite(multiplier) ? multiplier : 1.7);

    const story =
      document.querySelector('gr-landing-story[data-gr-blur-text]') ||
      document.querySelector('gr-landing-story');
    const target = story?.closest('.shopify-section') || story;
    if (target instanceof HTMLElement) {
      const y =
        (typeof this.#engine?.lenis?.scroll === 'number'
          ? this.#engine.lenis.scroll
          : window.scrollY || 0) + target.getBoundingClientRect().top;
      return Math.max(0, Math.round(Math.max(fadeEnd, y)));
    }

    return Math.max(0, Math.round(fadeEnd));
  }

  
  #rearmGate() {
    const engine = this.#engine;
    if (!engine || !this.#isInteractive()) return;

    if (this.#introTimeout) {
      clearTimeout(this.#introTimeout);
      this.#introTimeout = null;
    }

    window.removeEventListener('wheel', this.#preventScroll);
    window.removeEventListener('touchmove', this.#preventScroll);

    engine.stop();
    window.scrollTo(0, 0);

    this.#introAnimating = false;
    this.#introComplete = false;
    window.__grHeroAnimating = false;

    this.classList.remove('gr-hero--intro-active', 'gr-hero--intro-complete');
    document.documentElement.classList.remove(
      'gr-hero-intro-active',
      'gr-hero-intro-complete',
      'gr-hero-scrolled'
    );
    document.documentElement.classList.add('gr-hero-gate');

    const hint = this.refs.hint;
    if (hint && this.#gsap) {
      this.#gsap.set(hint, { opacity: 1 });
    }

    requestAnimationFrame(() => {
      this.#removeGateListeners();
      this.#armGateListeners();
    });
  }

  #armGateListeners() {
    document.addEventListener('wheel', this.#onFirstInteraction, CAPTURE_LISTENER);
    document.addEventListener('touchstart', this.#onFirstInteraction, CAPTURE_LISTENER);
    document.addEventListener('keydown', this.#onKeyDown, CAPTURE_LISTENER);
  }

  #setupScrollTriggers() {
    const root = this.refs.root;
    const mask = this.refs.mask;
    const ScrollTrigger = this.#ScrollTrigger;
    const gsap = this.#gsap;

    if (!root || !ScrollTrigger || !gsap) return;

    this.#triggers.push(
      ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        onLeave: () => this.#setHeaderVisible(true),
        onEnterBack: () => this.#setHeaderVisible(false),
      })
    );

    if (mask) {
      this.#triggers.push(
        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
          onLeave: () => this.#setMaskVisible(true),
          onEnterBack: () => this.#setMaskVisible(false),
        })
      );
    }

    this.#triggers.push(
      ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: 'bottom+=1000 top',
        onLeave: () => gsap.set(root, { opacity: 0 }),
        onEnterBack: () => gsap.set(root, { opacity: 1 }),
      })
    );
  }

  
  #setHeaderVisible(visible) {
    if (!document.body.classList.contains('template-the-abyss')) return;

    document.documentElement.classList.toggle('gr-hero-scrolled', visible);
    window.dispatchEvent(
      new CustomEvent('gr-hero:header-visible', { detail: { visible } })
    );
  }

  
  #setMaskVisible(visible) {
    document.documentElement.classList.toggle('gr-hero-mask-visible', visible);
    this.refs.mask?.toggleAttribute('hidden', !visible);

    const video = this.refs.mask?.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      if (visible) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    window.dispatchEvent(
      new CustomEvent('gr-hero:mask-visible', { detail: { visible } })
    );
  }

  #teardown() {
    if (this.#onScrollReady) {
      window.removeEventListener('gr-scroll:ready', this.#onScrollReady);
    }

    if (this.#onReinit) {
      window.removeEventListener('gr-hero:reinit', this.#onReinit);
    }

    this.#removeGateListeners();

    if (this.#preventScroll) {
      window.removeEventListener('wheel', this.#preventScroll);
      window.removeEventListener('touchmove', this.#preventScroll);
    }

    const hint = this.refs.hint;
    if (hint instanceof HTMLAnchorElement && this.#onHintClick) {
      hint.removeEventListener('click', this.#onHintClick);
    }

    const enter = this.refs.enter;
    if (enter instanceof HTMLButtonElement && this.#onEnterClick) {
      enter.removeEventListener('click', this.#onEnterClick);
    }

    const inner = this.refs.inner;
    if (inner instanceof HTMLElement) {
      inner.removeEventListener('click', this.#handleEnterClick);
    }

    if (this.#onFallbackEnter) {
      const enter = this.refs.enter;
      const hint = this.refs.hint;
      enter?.removeEventListener('click', this.#onFallbackEnter);
      hint?.removeEventListener('click', this.#onFallbackEnter);
      inner?.removeEventListener('click', this.#onFallbackEnter);
    }

    if (this.#introTimeout) clearTimeout(this.#introTimeout);
    if (this.#gateFallbackTimeout) clearTimeout(this.#gateFallbackTimeout);

    this.#triggers.forEach((trigger) => trigger.kill());
    this.#triggers = [];

    document.documentElement.classList.remove(
      'gr-hero-active',
      'gr-hero-gate',
      'gr-hero-scrolled',
      'gr-hero-mask-visible',
      'gr-hero-intro-active',
      'gr-hero-intro-complete'
    );

    window.__grHeroAnimating = false;

    this.#engine?.start();
    this.#engine = null;
    this.#gsap = null;
    this.#ScrollTrigger = null;
    this.#initialized = false;
  }
}

if (!customElements.get('gr-hero')) {
  customElements.define('gr-hero', GrHero);
}
